import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PageBody } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ApiError, adminApi } from '@/lib/api'

/** Who verifies. The nick is the whole record and what they see in their header. */
export function ReviewersPage() {
  const client = useQueryClient()
  const reviewers = useQuery({ queryKey: ['admin-reviewers'], queryFn: adminApi.reviewers })
  const [nick, setNick] = useState('')

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['admin-reviewers'] })
    void client.invalidateQueries({ queryKey: ['admin-invites'] })
  }

  const create = useMutation({
    mutationFn: () => adminApi.createReviewer(nick.trim()),
    onSuccess: () => {
      setNick('')
      refresh()
    },
    onError: (error) =>
      toast.error('Nie udało się dodać', {
        description: error instanceof Error ? error.message : undefined,
      }),
  })

  /**
   * Two gates before anything is lost: the plain confirm, and then whatever the
   * server says about answers this reviewer already wrote. The second one only
   * appears when there is something to lose, and it says how much.
   */
  const remove = useMutation({
    mutationFn: async (reviewer: { id: string; nick: string }) => {
      try {
        return await adminApi.deleteReviewer(reviewer.id, false)
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          if (!window.confirm(`${error.message}. Usunąć mimo to?`)) return null
          return adminApi.deleteReviewer(reviewer.id, true)
        }
        throw error
      }
    },
    onSuccess: (result) => {
      if (!result) return
      toast.success(
        result.deletedAnswers > 0
          ? `Usunięto razem z ${result.deletedAnswers} odpowiedziami`
          : 'Usunięto recenzenta',
      )
      refresh()
    },
    onError: (error) =>
      toast.error('Nie udało się usunąć', {
        description: error instanceof Error ? error.message : undefined,
      }),
  })

  const rows = reviewers.data ?? []

  return (
    <PageBody className="grid gap-5 lg:grid-cols-[1fr_320px]">
      <Card>
        <CardHeader>
          <CardTitle>Recenzenci</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Nikogo jeszcze nie ma</EmptyTitle>
                <EmptyDescription>Dodaj nick, a potem wygeneruj link.</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nick</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((reviewer) => (
                  <TableRow key={reviewer.id}>
                    <TableCell className="font-medium">{reviewer.nick}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={remove.isPending}
                        className="text-destructive hover:bg-clay-light hover:text-clay-dark"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Usunąć recenzenta ${reviewer.nick}? Zniknie razem ze swoimi linkami.`,
                            )
                          ) {
                            remove.mutate(reviewer)
                          }
                        }}
                      >
                        Usuń
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="text-sm">Dodaj recenzenta</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              if (nick.trim()) create.mutate()
            }}
          >
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="nick">Nick</FieldLabel>
                <Input
                  id="nick"
                  placeholder="np. haniasiecz"
                  value={nick}
                  onChange={(event) => setNick(event.target.value)}
                />
                <FieldDescription>
                  To jedyne, co recenzent o sobie widzi w nagłówku.
                </FieldDescription>
              </Field>
              <Button type="submit" disabled={create.isPending || !nick.trim()}>
                Dodaj
              </Button>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </PageBody>
  )
}
